import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../lib/api';
import { isSuperAdmin } from '../lib/auth';
import { copy } from '../lib/copy';
import { useToast } from '../context/ToastContext';
import { ErrorState } from './ui';

export function ProviderKeysPanel() {
  const queryClient = useQueryClient();
  const { pushToast } = useToast();
  const canEdit = isSuperAdmin();
  const [drafts, setDrafts] = useState<Record<string, { api_key: string; base_url: string }>>({});

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['providers'],
    queryFn: () => adminApi.providers(),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      provider,
      payload,
    }: {
      provider: string;
      payload: { api_key?: string; base_url?: string; is_enabled?: boolean };
    }) => adminApi.updateProvider(provider, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['providers'] });
      queryClient.invalidateQueries({ queryKey: ['models'] });
      pushToast(copy.models.providerSaved, 'success');
    },
    onError: () => {
      pushToast(copy.errors.updateProvider, 'error');
    },
  });

  const resyncMutation = useMutation({
    mutationFn: () => adminApi.resyncModels(),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['models'] });
      queryClient.invalidateQueries({ queryKey: ['providers'] });
      pushToast(copy.models.resyncDone(result.synced), 'success');
    },
    onError: () => {
      pushToast(copy.errors.resyncModels, 'error');
    },
  });

  if (isLoading) {
    return <p className="mb-4 text-sm text-ops-muted">{copy.common.loading}</p>;
  }

  if (isError) {
    return (
      <div className="mb-6">
        <ErrorState message={copy.errors.loadProviders} onRetry={() => refetch()} />
      </div>
    );
  }

  return (
    <div className="ops-card mb-6 space-y-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-ops-text">{copy.models.providersTitle}</h2>
          <p className="text-sm text-ops-muted">{copy.models.providersDescription}</p>
        </div>
        {canEdit ? (
          <button
            type="button"
            className="rounded-lg border border-ops-border px-3 py-2 text-sm text-ops-text hover:bg-ops-accent-soft disabled:opacity-50"
            disabled={resyncMutation.isPending}
            onClick={() => resyncMutation.mutate()}
          >
            {resyncMutation.isPending ? copy.models.resyncing : copy.models.resync}
          </button>
        ) : null}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {(data?.items ?? []).map((provider) => {
          const draft = drafts[provider.provider] ?? {
            api_key: '',
            base_url: provider.base_url,
          };
          return (
            <div key={provider.provider} className="rounded-lg border border-ops-border p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="font-medium text-ops-text">{provider.display_name}</p>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs ${
                    provider.configured
                      ? 'bg-ops-accent-soft text-ops-text'
                      : 'bg-ops-danger-soft text-ops-danger'
                  }`}
                >
                  {provider.configured ? copy.models.providerConfigured : copy.models.providerMissing}
                </span>
              </div>
              <p className="mb-3 text-xs text-ops-muted">
                {copy.models.providerSource(provider.source)}
                {provider.api_key_hint ? ` · ${provider.api_key_hint}` : ''}
              </p>
              <label className="mb-2 block text-xs text-ops-muted">
                {copy.models.providerBaseUrl}
                <input
                  type="url"
                  value={draft.base_url}
                  disabled={!canEdit}
                  onChange={(event) =>
                    setDrafts((prev) => ({
                      ...prev,
                      [provider.provider]: { ...draft, base_url: event.target.value },
                    }))
                  }
                  className="mt-1 w-full rounded-lg border border-ops-border bg-ops-surface px-3 py-2 text-sm text-ops-text"
                />
              </label>
              <label className="mb-3 block text-xs text-ops-muted">
                {copy.models.providerApiKey}
                <input
                  type="password"
                  value={draft.api_key}
                  disabled={!canEdit}
                  placeholder={provider.configured ? copy.models.providerKeyPlaceholderSet : copy.models.providerKeyPlaceholder}
                  onChange={(event) =>
                    setDrafts((prev) => ({
                      ...prev,
                      [provider.provider]: { ...draft, api_key: event.target.value },
                    }))
                  }
                  className="mt-1 w-full rounded-lg border border-ops-border bg-ops-surface px-3 py-2 text-sm text-ops-text"
                />
              </label>
              {canEdit ? (
                <button
                  type="button"
                  className="w-full rounded-lg bg-ops-accent px-3 py-2 text-sm text-white disabled:opacity-50"
                  disabled={updateMutation.isPending}
                  onClick={() =>
                    updateMutation.mutate({
                      provider: provider.provider,
                      payload: {
                        base_url: draft.base_url,
                        ...(draft.api_key.trim() ? { api_key: draft.api_key.trim() } : {}),
                      },
                    })
                  }
                >
                  {copy.models.providerSave}
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
